/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines */

// deps
import React, { memo, useCallback, useContext, useRef, useEffect, useMemo, useState } from "react";

// internal-deps
import {
    useEffectOnce,
    clearTimerFromStorage,
    LocalStorage,
    LOCAL_STORAGE_KEYS,
    scrollElementIntoViewWithOffset,
    Source,
    RouteProps,
    ElementObserver,
} from "@swiggy-private/core-ui";
import {
    CouponModalWrapper,
    EditNumberWidget,
    DelightPopup,
    CART_STATUS_CODES,
    GenericCartToast,
    ToingPopup,
    FreeCashRemovalPopup,
} from "@swiggy-private/cart-ui";
import {
    CouponConfetti,
    ValueDrivenCrossSellXP,
    TDSuccessCardType,
    PC_HALFCARD_STATES,
    FreeCashRemovalPopupVariant,
} from "@swiggy-private/cart-types";
import {
    GlobalHeaderState,
    SwgyHeaderKeysList,
    AnalyticsContext,
    CheckoutContext,
    CheckoutActionTypes,
    CheckoutRoute,
    getBridgeSdk,
    UserContext,
    recordNREvent,
    getUserFromUserContextValue,
    VisualEffectContext,
    NEWRELIC_TABLES,
    GroupOrderCartContext,
    CouponPageType,
    goToHome,
} from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

//styles
import { OfoWidgetContainer, CornerWrapper, RadialElement, ObserverNoWidthStyles, MarginBottomDiv } from "./styles";

// components
import { CartFooter } from "@cart-client/components/cart-footer";
import { UpdateProgressLoader } from "@swiggy-private/core-ui";
import CartUIWrapper from "./cart-ui-wrapper";
import { CartTimerWrapper } from "@cart-client/components/cart-timer-wrapper";
import { CartWidgetRenderer } from "@cart-client/components/cart-widget-renderer";
import { AddressListManager } from "@cart-client/components/address-list-manager";
import GenericCroutonWrapper from "@cart-client/components/generic-crouton-wrapper/GenericCroutonWrapper";
import { CancellationPolicyWidget } from "@swiggy-private/core-ui";
import CartLoaderAndErrorRenderer from "./CartLoaderAndError";
import { CartStressCommsWrapper } from "@cart-client/components/stress-comms-wrapper";
import ToingPriceComparisonHalfCard from "../toing-price-comparison-halfcard/ToingPriceComparisonHalfCard";

// constants
import { TD_COUPON_MODAL } from "@cart-client/constants/Coupon";
import { confettiMapping } from "@cart-client/constants/Coupon";
import {
    CART_ELEMENT_IDS,
    SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
    SCROLL_MARGIN_TOP_WITHOUT_BANNER,
} from "@cart-client/components/cart-renderer/constants";

// helpers
import {
    getMenuItems,
    getCouponErrorCard,
    getUnServiceableItemsCount,
    getCartItemsCount,
    getRestaurantName,
    getRestaurantId,
    getSavingsBannerData,
    getQFoodSLA,
    getCartId,
    getTradeDiscountCouponCardUI,
    handleAddMoreItemsClick,
    isPORV2Flow,
    isCartEmpty,
} from "@cart-client/helpers/Cart";
import { isBulkOrder } from "@cart-client/helpers/BulkOrder";
import { isClosingTimeAvailable, setClosingTimeInStorage } from "@cart-client/helpers/TimerOnCart";
import { isTurboPaymentEnabled } from "@cart-client/helpers/TurboPayment";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
import { syncCartAddressHeaders } from "@cart-client/helpers/Header";
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { logCartViewedEvent } from "@cart-client/helpers/Clevertap";
import { recordCartPercievedTime } from "@cart-client/helpers/Metric";
import { isItemExceededLimitMessageExists, isOnTimeCart } from "@cart-client/helpers/CartItemRenderer";

// hooks
import useFetchCart from "@cart-client/hooks/useFetchCart";
import { useCartToast } from "@cart-client/hooks/useCartToast";
import { useFetchAddressOnClick } from "@cart-client/hooks/useFetchAddressOnClick";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
import { useCrossSellAnalytics } from "@cart-client/hooks/useCrossSellAnalytics";
import { useIPLSwiggySixesCartCroutonAndUpdates } from "@cart-client/hooks/useIPLSwiggySixesCartCroutonAndUpdates";
import { useIPLSwiggySixesTimerCrouton } from "@cart-client/hooks/useIPLSwiggySixesTimerCrouton";
import { usePrefetchToingLottie } from "@cart-client/hooks/usePrefetchToingLottie";
import { usePrefetchToingPriceComparisonLotties } from "@cart-client/hooks/usePrefetchToingPriceComparisonLotties";
import { useGenericCrouton } from "@cart-client/hooks/useGenericCrouton";
import useToingPopup from "@cart-client/hooks/useToingPopup";
import useToingPriceComparison from "@cart-client/hooks/useToingPriceComparison/useToingPriceComparison";

// reducers
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartToastContext } from "@cart-client/contexts/CartToastContext";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";
import { useFreeCashRemovalPopup } from "@cart-client/contexts/FreeCashRemovalPopupContext";

//analytics
import { clickViewDetailedBill, clickAddMoreItemsCta, onWebcartUpdateImpression } from "@cart-client/analytics/events";
import useAutoApplyCallout from "@cart-client/hooks/useAutoApplyCallout";
import { impressionCancellationPolicyWidget } from "@cart-client/analytics/events";

// interfaces
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";
import useCartPayment from "@cart-client/hooks/useCartPayment";
import { useOfoWidgetData } from "@cart-client/hooks/useOfoWidgetData";

function ToingCartRenderer(_props: RouteProps<CheckoutRoute>): React.ReactElement {
    const [intialCartLoaded, setInitalCartLoaded] = useState(false);
    const { loading, localCartState, reviewedCartState, error: cartApiError } = useFetchCart();
    const { cartState, setCartState } = useContext(CartContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { resetCouponListingPage } = useContext(CouponListingContext);
    const { showConfetti } = useContext(VisualEffectContext);
    const { user: userContextValue } = useContext(UserContext);
    const { state: groupCartState, isGroupCartFlow } = useContext(GroupOrderCartContext);
    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);

    const [isTurboPayWidgetHidden, setIsTurboPayWidgetHidden] = useState(false);

    const { reviewedCart: cartResponse } = cartState;
    const { cartFeedbackXP, valueDrivenCrossSellXPVariant, porFlowXP } = useCartRendererXP(cartResponse);

    const { canPlaceOrderFromCart, onMakePayment } = useCartPayment();
    const { popupConfig, showPopup, hidePopup } = useFreeCashRemovalPopup();

    const {
        impressionAutoApplyCouponPopup,
        impressionOffercashSuccessPopup,
        impressionDelightPopup,
        onClickCloseDelightPopup,
        onTimerLoad,
        onClickCouponSavingsPopup,
        onClickCouponSavingsPopupWithPOR,
        onImpressionMoreOffersStrip,
        onClickMoreOffersStrip,
        onClickCouponMaxSavingsPopup,
    } = useCartRendererAnalytics(cartResponse);
    usePrefetchToingLottie();

    const { onClickAddMoreAndSaveMore } = useCrossSellAnalytics(cartResponse);

    const [isCroutonVisible, setIsCroutonVisible] = useState(false);

    const setCroutonVisibility = useCallback((croutonVisibility: boolean) => {
        setIsCroutonVisible(croutonVisibility);
    }, []);

    useEffect(() => {
        setIsTurboPayWidgetHidden(false);
    }, [cartState.uniqueIdForCart]);

    const { croutonData, allowIPLCroutonOverRestaurantTimer } = useIPLSwiggySixesCartCroutonAndUpdates(cartResponse);
    useIPLSwiggySixesTimerCrouton({ croutonData, setCroutonVisibility });

    const { updateCartInProgress, reloadCart, showCouponModal, toggleCouponModal, showPendingDiscountingComms } =
        useContext(UpdateCartContext);

    const showCartToast = useCartToast(cartResponse, isCroutonVisible);
    const { cartToastData } = useContext(CartToastContext);
    const {
        showDelightPopup,
        couponSuccessData,
        isCouponAutoApplied,
        tradeDiscountSuccessInfo,
        handleCloseDelightPopup,
    } = useAutoApplyCallout(cartResponse, toggleCouponModal);

    const { showToingPopup, handleToingPopupClose, onImpressionToingPopup } = useToingPopup();

    const {
        pcHalfCardState,
        closeHalfCard,
        closeHalfCardSilent,
        submitImage,
        isHalfCardOpen,
        openHalfCard,
        presignedData,
        updatePcHalfCardState,
        priceComparisonData,
        isFreeCashApplicable,
        totalSavings,
    } = useToingPriceComparison();

    const showFooterOverHalfCard = useMemo(() => {
        return (
            isHalfCardOpen &&
            pcHalfCardState === PC_HALFCARD_STATES.SUCCESS &&
            isEmpty(priceComparisonData?.rejection_state_halfcard_data)
        );
    }, [isHalfCardOpen, pcHalfCardState, priceComparisonData]);

    usePrefetchToingPriceComparisonLotties(priceComparisonData);

    const isCartStatusSuccess = useMemo(
        () => cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS,
        [cartResponse?.statusCode],
    );

    const { displayAddressList, goToSelectAddress, selectedAddress } = useFetchAddressOnClick(
        cartResponse?.data?.cart_total,
        cartResponse?.data?.addresses,
        isCartStatusSuccess,
    );

    const qFoodSLA = useMemo(() => getQFoodSLA(cartResponse), [cartResponse]);
    const userLoggedIn = isUserLoggedIn();

    const tradeDiscountCouponInfo = getTradeDiscountCouponCardUI(cartResponse);

    const makePaymentButtonRef = useRef<HTMLDivElement>(null);
    const planAddedRef = useRef<HTMLDivElement>(null);
    const isFromBottomBar = GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar) === "true";
    const sessionId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "0";
    const showCouponConfetti = LocalStorage.getItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN) !== sessionId;

    const isValueMeal = tradeDiscountCouponInfo?.td_success_card_type === TDSuccessCardType.OFFER_CARD_TYPE_VALUE_MEALS;
    const disableRedirectionToMenu = isValueMeal;

    const tDCouponModal = LocalStorage.getItem<{ [key: string]: string }>(TD_COUPON_MODAL.TD_COUPON_MODAL);
    const [showTradeDiscountCoupon, setShowTradeDiscountCoupon] = useState(false);
    const onClickTDCouponModal = useCallback(() => {
        setShowTradeDiscountCoupon(false);
        LocalStorage.setItem(TD_COUPON_MODAL.TD_COUPON_MODAL, {
            restaurantId: getRestaurantId(cartResponse),
            savings: tradeDiscountCouponInfo?.savings || "",
        });
    }, [cartResponse, tradeDiscountCouponInfo?.savings]);

    useEffect(() => {
        if (
            !isEmpty(tradeDiscountCouponInfo) &&
            (tDCouponModal?.restaurantId !== getRestaurantId(cartResponse) ||
                tDCouponModal?.savings !== tradeDiscountCouponInfo?.savings)
        )
            setShowTradeDiscountCoupon(true);
    }, [cartResponse, tDCouponModal, tradeDiscountCouponInfo]);
    // Only show timer if servecieable, bcz backend always sends the time even in error cases.
    // special handling for IPL crouton case where it might take priority when ipl timer < restaurant timer
    const canShowTimer =
        cartResponse?.statusCode === 0 && isClosingTimeAvailable(cartResponse) && !allowIPLCroutonOverRestaurantTimer;

    // Enable Deal Rush and other generic croutons in Toing cart
    useGenericCrouton({ setCroutonVisibility, canShowTimer, uploadId: presignedData?.request_id });

    const savingsBannerData = getSavingsBannerData(cartResponse);

    const canShowFooterCrouton = useMemo(
        () => !canShowTimer && !isItemExceededLimitMessageExists(cartResponse),
        [canShowTimer, cartResponse],
    );

    const { canShowOFOWidget, ...ofowidgetData } = useOfoWidgetData({
        selectedAddress,
        localCartState,
    });

    const [isPageScrolled, setPageScrolled] = useState(false);

    useEffect(() => {
        // set closing time in storage
        setClosingTimeInStorage(cartResponse);
    }, [cartResponse]);

    const onTimerEnd = useCallback(async () => {
        clearTimerFromStorage();
        reloadCart();
        recordNREvent({ eventType: NEWRELIC_TABLES.TIMER_END_UPDATE_CART_CALLED });
    }, [reloadCart]);

    const turboPaymentEnabled = useMemo(() => {
        return isTurboPaymentEnabled(cartResponse, isTurboPayWidgetHidden);
    }, [cartResponse, isTurboPayWidgetHidden]);

    const handleCartModalClose = useCallback(() => {
        toggleCouponModal();
        if (showCouponModal) {
            showPendingDiscountingComms();
        }
    }, [toggleCouponModal, showCouponModal, showPendingDiscountingComms]);

    const viewDetailedBill = useCallback(() => {
        clickViewDetailedBill(cartResponse?.data?.cart_total ?? 0, clickEvent);
        scrollElementIntoViewWithOffset(
            CART_ELEMENT_IDS.BILL_DETAILS_CONTAINER,
            isEmpty(savingsBannerData) ? SCROLL_MARGIN_TOP_WITHOUT_BANNER : SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
            true,
            CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_SELECTOR,
        );
    }, [cartResponse, clickEvent, savingsBannerData]);

    const isBulkOrderFn = useMemo(() => {
        return isBulkOrder(cartResponse);
    }, [cartResponse]);

    useEffect(() => {
        if (!loading && reviewedCartState != null && intialCartLoaded === false) {
            const payload = {
                uniqueIdForCart: randomStringIdGenerator(),
                localCart: localCartState,
                reviewedCart: reviewedCartState,
            };

            // log clevertap event for cart viewed
            logCartViewedEvent(reviewedCartState);

            recordCartPercievedTime();

            // update cart state
            setCartState({ type: CartActionTypes.UPDATE_CART, payload });
            setInitalCartLoaded(true);
        }
    }, [localCartState, reviewedCartState, loading, setCartState, user, intialCartLoaded]);

    useEffectOnce(() => {
        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({
                show: true,
            });
        }
    });

    const navigateToMenu = useCallback(() => {
        const cartId = String(cartResponse?.data?.cartId ?? "");
        if (valueDrivenCrossSellXPVariant === ValueDrivenCrossSellXP.ADD_MORE_ITEMS_TEXT) {
            onClickAddMoreAndSaveMore(false);
        } else {
            clickAddMoreItemsCta(clickEvent, cartId);
        }
        updateStatusBarCleanup();
        handleAddMoreItemsClick({ isGroupCartFlow, isFromBottomBar, groupCartState, cartResponse });
    }, [
        cartResponse,
        clickEvent,
        groupCartState,
        isFromBottomBar,
        isGroupCartFlow,
        onClickAddMoreAndSaveMore,
        valueDrivenCrossSellXPVariant,
    ]);

    const goToMenu = useCallback(() => {
        if (isFreeCashApplicable) {
            showPopup({
                variant: FreeCashRemovalPopupVariant.MENU_NAVIGATION,
                onPrimaryClick: () => {
                    hidePopup();
                    navigateToMenu();
                },
                onSecondaryClick: hidePopup,
                analyticsParams: {
                    warningTrigger: "menu_navigation",
                },
            });
            return;
        }
        navigateToMenu();
    }, [isFreeCashApplicable, showPopup, hidePopup, navigateToMenu]);

    const showAddressWidgetTop = useMemo(() => userLoggedIn && selectedAddress, [userLoggedIn, selectedAddress]);

    const triggerCouponConfetti = useCallback(
        (type: CouponConfetti) => {
            switch (type) {
                case CouponConfetti.TOP_UP:
                    showConfetti(confettiMapping.CONFETTI_TYPE_TOP_UP);
                    LocalStorage.setItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN, sessionId);
                    break;
                case CouponConfetti.AUTO_APPLY:
                    showConfetti(confettiMapping.CONFETTI_TYPE_AUTO_APPLY);
                    LocalStorage.setItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN, sessionId);
                    break;
                case CouponConfetti.INVALID:
                default:
                    break;
            }
        },
        [showConfetti, sessionId],
    );

    const setScrolledState = useCallback(() => {
        setPageScrolled(true);
    }, []);

    const unsetScrolledState = useCallback(() => {
        setPageScrolled(false);
    }, []);

    const goToCouponListing = useCallback(
        (couponListingType: CouponPageType) => {
            resetCouponListingPage();

            setCheckoutState({
                type: CheckoutActionTypes.SHOW_COUPON_LISTING,
                payload: {
                    couponListingLaunchTimestamp: Date.now(),
                    couponListingType,
                },
            });
        },
        [resetCouponListingPage, setCheckoutState],
    );

    const handleViewEnter = useCallback(() => {
        impressionCancellationPolicyWidget({ impressionEvent, cartId: cartResponse?.data?.cartId });
    }, [impressionEvent, cartResponse?.data?.cartId]);

    useEffect(() => {
        if (checkoutState.type !== CheckoutActionTypes.SHOW_CART) {
            return;
        }

        if (turboPaymentEnabled) {
            const cartId = cartResponse?.data?.cartId || "";
            const addressId = cartResponse?.data?.address_id || "";

            syncCartAddressHeaders({ cartId, addressId });
        }
    }, [cartResponse?.data?.address_id, cartResponse?.data?.cartId, turboPaymentEnabled, checkoutState]);

    useEffect(() => {
        if (cartResponse && cartResponse?.data) {
            const cartId = cartResponse?.data?.cartId || "";
            onWebcartUpdateImpression(impressionEvent, {
                cartId,
                restaurantId: getRestaurantId(cartResponse),
                cartTotal: cartResponse?.data.cart_total,
                couponCode: cartResponse?.data?.coupon_code,
                couponApplied: !!cartState.localCart?.isCouponValid,
                turboPaymentEnabled,
                xpValues: JSON.stringify(cartResponse?.data.XPData),
                groupCartId: groupCartState?.data?.group_cart_id,
            });
        }
    }, [
        cartResponse,
        cartResponse?.data,
        cartState.localCart?.isCouponValid,
        groupCartState.data?.group_cart_id,
        impressionEvent,
        isGroupCartFlow,
        turboPaymentEnabled,
    ]);

    if (isEmpty(cartResponse) || isEmpty(cartResponse?.data) || isCartEmpty(cartResponse?.data)) {
        return (
            <CartLoaderAndErrorRenderer
                disableRedirectionToMenu={disableRedirectionToMenu}
                loading={loading}
                cartApiError={cartApiError}
                isFromBottomBar={isFromBottomBar}
            />
        );
    }

    const couponErrorData = getCouponErrorCard(cartResponse.data);
    const isNewPORFlow = isPORV2Flow(porFlowXP);

    return (
        <CartUIWrapper
            disableRedirectionToMenu={disableRedirectionToMenu}
            isFreeCashApplicable={isFreeCashApplicable}
            containerId={CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_ID}
            showCartFeedback={cartFeedbackXP}
            statusCode={cartResponse.statusCode}
            restaurantName={getRestaurantName(cartResponse)}
            cartId={getCartId(cartResponse)}
            restaurantId={getRestaurantId(cartResponse)}
            itemCount={getCartItemsCount(cartResponse)}
            isPageScrolled={isPageScrolled}
            selectedAddress={selectedAddress}
            onAddressDropdownClick={goToSelectAddress}
            qFoodSLA={qFoodSLA}
            showAddressWidgetTop={!!showAddressWidgetTop}
            showGreenSubtitle={isOnTimeCart(cartResponse?.data?.promises)}
            isSavingsBannerExist={!isEmpty(savingsBannerData)}
            goToHome={goToHome}
            openHalfCard={openHalfCard}
            uploadId={presignedData?.request_id}
        >
            <CornerWrapper>
                <RadialElement $isLeft />
            </CornerWrapper>
            <CornerWrapper $isRight>
                <RadialElement $isRight />
            </CornerWrapper>
            <ElementObserver
                onViewEnter={unsetScrolledState}
                observerStyle={ObserverNoWidthStyles}
                onViewExit={setScrolledState}
            >
                <MarginBottomDiv />
            </ElementObserver>
            <div>
                {canShowOFOWidget && (
                    <OfoWidgetContainer>
                        <EditNumberWidget {...ofowidgetData} />
                    </OfoWidgetContainer>
                )}
                {(showCouponModal || showTradeDiscountCoupon) && (
                    <CouponModalWrapper
                        couponErrorData={couponErrorData}
                        couponSuccessData={couponSuccessData}
                        closeModal={handleCartModalClose}
                        onClickCouponSavingsPopup={onClickCouponSavingsPopup}
                        onClickCouponSavingsPopupWithPOR={onClickCouponSavingsPopupWithPOR}
                        triggerConfettiByType={triggerCouponConfetti}
                        isCouponAutoApplied={isCouponAutoApplied}
                        impressionAutoApplyCouponPopup={impressionAutoApplyCouponPopup}
                        impressionOffercashSuccessPopup={impressionOffercashSuccessPopup}
                        showCouponConfetti={showCouponConfetti}
                        tradeDiscountCouponInfo={tradeDiscountCouponInfo}
                        onClickTDCouponModal={onClickTDCouponModal}
                        isNewPORFlow={isNewPORFlow}
                        goToCouponListing={goToCouponListing}
                        onImpressionMoreOffersStrip={onImpressionMoreOffersStrip}
                        onClickMoreOffersStrip={onClickMoreOffersStrip}
                        onClickCouponMaxSavingsPopup={onClickCouponMaxSavingsPopup}
                    />
                )}
                {showDelightPopup && !isEmpty(tradeDiscountSuccessInfo) && (
                    <DelightPopup
                        data={tradeDiscountSuccessInfo}
                        closeModal={handleCloseDelightPopup}
                        onImpressionDelightPopup={impressionDelightPopup}
                        onClickCloseDelightPopup={onClickCloseDelightPopup}
                        isToingCart
                    />
                )}
                {showToingPopup && isEmpty(tradeDiscountSuccessInfo) && (
                    <ToingPopup closeModal={handleToingPopupClose} onImpressionToingPopup={onImpressionToingPopup} />
                )}
                {popupConfig && (
                    <FreeCashRemovalPopup
                        variant={popupConfig.variant}
                        closeModal={hidePopup}
                        onSecondaryClick={popupConfig.onSecondaryClick ?? hidePopup}
                        onPrimaryClick={popupConfig.onPrimaryClick}
                    />
                )}
                <CartStressCommsWrapper />
                <CartWidgetRenderer
                    canShowTimer={canShowTimer}
                    setCroutonVisibility={setCroutonVisibility}
                    planAddedRef={planAddedRef}
                    goToMenu={goToMenu}
                    isCroutonVisible={isCroutonVisible}
                    isValueMeal={isValueMeal}
                    hideSavingsSectionTitle
                />
                {/* <ScreenReaderOnly as="button" text={ACCESSIBLITY_TEXTS.SKIP_TO_PAYMENT} onClick={focusMakePaymentButton} /> */}
                {canShowTimer && (
                    <CartTimerWrapper
                        source={Source.CART}
                        onComplete={onTimerEnd}
                        onTimerLoad={onTimerLoad}
                        setCroutonVisibility={setCroutonVisibility}
                    />
                )}
                {showCartToast && <GenericCartToast toastData={cartToastData} />}

                <CancellationPolicyWidget
                    cancellationDisclaimerData={cartResponse?.data?.cancellation_policy_widget}
                    onViewEnter={handleViewEnter}
                />

                <CartFooter
                    addressId={cartResponse.data.address_id}
                    addresses={cartResponse.data.addresses}
                    cartTotal={cartResponse.data.cart_total}
                    crouton={cartResponse.data.crouton}
                    canShowCrouton={canShowFooterCrouton}
                    statusCode={cartResponse.statusCode}
                    statusMessage={cartResponse.statusMessage}
                    titleMessage={cartResponse.titleMessage}
                    onMakePayment={onMakePayment}
                    ctaText={cartResponse.ctaText}
                    ctaAction={cartResponse.ctaAction}
                    viewDetailedBill={viewDetailedBill}
                    cartItems={getMenuItems(cartResponse)}
                    unServiceableItemsCount={getUnServiceableItemsCount(cartResponse)}
                    canPlaceOrderFromCart={canPlaceOrderFromCart}
                    makePaymentButtonRef={makePaymentButtonRef}
                    qFoodSLA={qFoodSLA}
                    restaurantId={cartResponse.data.restaurant_details?.id}
                    cartId={cartResponse.data.cartId}
                    isTurboPayTooltipHidden={isCroutonVisible}
                    setCroutonVisibility={setCroutonVisibility}
                    isTurboPaymentEnabled={turboPaymentEnabled}
                    setIsTurboPayWidgetHidden={setIsTurboPayWidgetHidden}
                    updateCartInProgress={updateCartInProgress}
                    isBulkOrder={isBulkOrderFn}
                    // This is not needed for toing cart renderer. But as prop is required for cart footer, we are passing a empty function.
                    setForceExtraPaddingOnCart={() => undefined}
                    showFooterOverHalfCard={showFooterOverHalfCard}
                    closeHalfCard={closeHalfCard}
                />

                {isHalfCardOpen && (
                    <ToingPriceComparisonHalfCard
                        comparisonState={pcHalfCardState}
                        priceComparisonData={priceComparisonData}
                        onUploadBill={submitImage}
                        onClose={closeHalfCard}
                        onCloseSilent={closeHalfCardSilent}
                        presignedData={presignedData}
                        updatePcHalfCardState={updatePcHalfCardState}
                        totalSavings={totalSavings}
                    />
                )}

                <AddressListManager reviewedCart={cartResponse} displayAddressList={displayAddressList} />
                {updateCartInProgress && <UpdateProgressLoader />}
                <GenericCroutonWrapper />
            </div>
        </CartUIWrapper>
    );
}

export default memo(ToingCartRenderer);
